import { createSlice } from "@reduxjs/toolkit";
import {
  getGroupPosts,
  likePost,
  reactToPost,
  getGroupsList,
  getPost,
  addPost,
  likeComment,
  commentPost,
  getCommentAndReplies,
  replyComment,
  deletePost,
  toggleFavoritePost,
  recordPostShare,
} from "../../actions";
import groupsSliceFunctions, {
  replyCommentFunctions,
} from "./groupsSliceFunctions";
import { transitionReactionCounts } from "../../../services/commentReactions";

const {
  createFakeReply,
  replaceFakeReplyforReal,
  deleteFakeReply,
  increaseRepliesCountComment,
  decreaseRepliesCountComment,
} = replyCommentFunctions;
const { createFakeComment } = groupsSliceFunctions;

export const selectSegmentGroupSlice = (is_fixed) => {
  return is_fixed ? "fixed" : "general";
};
export const selectGroupSlice = (from_main_group) => {
  return from_main_group ? "main" : "not_main";
};

const builderGroupData = (action) => {
  const is_fixed = action.meta.arg.is_fixed;
  const from_main_group = action.meta.arg.from_main_group;
  const segmentSelected = selectSegmentGroupSlice(is_fixed);
  const groupSelected = selectGroupSlice(from_main_group);
  return { segmentSelected, groupSelected };
};

const likeActionElementId = (action) => action.meta.arg.element_id;

const postIndexByElementId = (posts, elementId) => {
  if (!Array.isArray(posts)) return -1;
  const want = String(elementId);
  return posts.findIndex((post) => String(post?.id) === want);
};

const updateCachedPostGroup = (state, elementId, updater) => {
  const want = String(elementId);
  const updateBucket = (bucket) => {
    const posts = bucket?.data?.posts;
    if (!Array.isArray(posts)) return;
    posts.forEach((post) => {
      if (String(post?.id) === want) updater(post);
    });
  };

  const root = state.getGroupPosts;
  updateBucket(root?.main?.fixed);
  updateBucket(root?.main?.general);
  updateBucket(root?.not_main?.fixed);
  updateBucket(root?.not_main?.general);

  const postGroup = state.getPost?.data?.post_group;
  if (postGroup && String(postGroup.id) === want) updater(postGroup);
};

/**
 * Feed cards read liked from Redux; getPost can hold the same post_group after
 * preview/detail navigation and was not updated by likePost — keep copies aligned.
 */
const syncGetPostLikeFromFeedIfSamePost = (state, action) => {
  const { segmentSelected, groupSelected } = builderGroupData(action);
  const posts =
    state.getGroupPosts[groupSelected]?.[segmentSelected]?.data?.posts;
  const elementId = likeActionElementId(action);
  const postIndex = postIndexByElementId(posts, elementId);
  if (postIndex < 0) return;

  const post = posts[postIndex];
  const pg = state.getPost?.data?.post_group;
  if (!post || !pg) return;
  if (String(post.id) !== String(pg.id)) return;

  pg.liked = post.liked;
  pg.likes_count = post.likes_count;
};

const updateLikePostGroup = (state, action) => {
  const { segmentSelected, groupSelected } = builderGroupData(action);

  const posts =
    state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
  const postIndex = postIndexByElementId(posts, likeActionElementId(action));
  if (postIndex >= 0) {
    const likedOldValue =
      state.getGroupPosts[groupSelected][segmentSelected].data.posts[postIndex]
        .liked;
    state.getGroupPosts[groupSelected][segmentSelected].data.posts[
      postIndex
    ].liked = !likedOldValue;
    state.getGroupPosts[groupSelected][segmentSelected].data.posts[
      postIndex
    ].likes_count += likedOldValue ? -1 : 1;
  }
};

const updateCommentCountPostGroup = (state, action, value) => {
  const { segmentSelected, groupSelected } = builderGroupData(action);

  const posts =
    state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
  const postIndex = postIndexByElementId(
    posts,
    action.meta.arg.element_id,
  );
  if (postIndex >= 0) {
    state.getGroupPosts[groupSelected][segmentSelected].data.posts[
      postIndex
    ].comments_count += value;
  }
};

const updateLikePost = (state, action) => {
  if (!action.meta.arg.only_post) {
    updateLikePostGroup(state, action);
    syncGetPostLikeFromFeedIfSamePost(state, action);
  } else {
    const group_Array = [true, false];

    for (const group_option of group_Array) {
      action.meta.arg.from_main_group = group_option;
      updateLikePostGroup(state, action);
    }

    if (state.getPost?.data?.post_group) {
      const likedOldValue = state.getPost.data.post_group.liked;
      state.getPost.data.post_group.liked = !likedOldValue;
      state.getPost.data.post_group.likes_count += likedOldValue ? -1 : 1;
    }
  }
};

// Apply a reaction transition the caller already computed (delta + willBeLiked
// + reaction_type). Unlike updateLikePost's blind toggle, this stays correct
// when swapping one reaction for another (delta 0, still liked).
const applyReactToPost = (post, action) => {
  if (!post) return;
  const {
    delta = 0,
    willBeLiked,
    reaction_type,
    prevReaction = null,
  } = action.meta.arg;
  const nextReaction = willBeLiked ? reaction_type : null;
  if (typeof willBeLiked === "boolean") post.liked = willBeLiked;
  post.likes_count = Math.max(0, (post.likes_count || 0) + delta);
  post.my_reaction = nextReaction;

  if (prevReaction !== nextReaction) {
    post.reactions = transitionReactionCounts(
      post.reactions,
      prevReaction,
      nextReaction,
    );
  }
};

const updateReactPostGroup = (state, action) => {
  const { segmentSelected, groupSelected } = builderGroupData(action);
  const posts =
    state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
  const postIndex = postIndexByElementId(posts, likeActionElementId(action));
  if (postIndex >= 0) {
    applyReactToPost(
      state.getGroupPosts[groupSelected][segmentSelected].data.posts[postIndex],
      action,
    );
  }
};

const updateReactPost = (state, action) => {
  if (!action.meta.arg.only_post) {
    updateReactPostGroup(state, action);
    const pg = state.getPost?.data?.post_group;
    if (pg && String(pg.id) === String(likeActionElementId(action))) {
      applyReactToPost(pg, action);
    }
  } else {
    for (const group_option of [true, false]) {
      action.meta.arg.from_main_group = group_option;
      updateReactPostGroup(state, action);
    }
    applyReactToPost(state.getPost?.data?.post_group, action);
  }
};

const reactionTypeToReplace = (reactions, requestedType, guessedType) => {
  if (
    guessedType &&
    guessedType !== requestedType &&
    Number(reactions?.[guessedType]) > 0
  ) {
    return guessedType;
  }

  return Object.entries(reactions || {})
    .filter(
      ([type, count]) =>
        type !== requestedType && Number(count) > 0,
    )
    .sort(([, countA], [, countB]) => Number(countB) - Number(countA))[0]?.[0];
};

// The likes endpoint returns only a message ("inserted" or "deleted"). Use it
// to reconcile the optimistic transition against the server's actual outcome.
// This is important for legacy feed payloads that say `liked: true` but omit
// which reaction the current user selected.
const reconcileReactToPost = (post, action) => {
  if (!post) return;
  const message = String(action.payload?.message ?? "").toLowerCase();
  const wasDeleted = message.includes("deleted");
  const wasInserted = message.includes("inserted");
  if (!wasDeleted && !wasInserted) return;

  const {
    reaction_type,
    prevLiked,
    prevReaction,
    prevLikesCount = 0,
    prevReactions = {},
  } = action.meta.arg;

  post.liked = !wasDeleted;
  post.likes_count = Math.max(
    0,
    Number(prevLikesCount || 0) +
      (wasDeleted ? -1 : prevLiked ? 0 : 1),
  );
  post.my_reaction = wasDeleted ? null : reaction_type;

  if (wasDeleted) {
    post.reactions = transitionReactionCounts(
      prevReactions,
      reaction_type,
      null,
    );
    return;
  }

  const reactionBeingReplaced = prevLiked
    ? reactionTypeToReplace(
        prevReactions,
        reaction_type,
        prevReaction,
      )
    : null;
  post.reactions = transitionReactionCounts(
    prevReactions,
    reactionBeingReplaced,
    reaction_type,
  );
};

const reconcileReactPost = (state, action) => {
  if (!action.meta.arg.only_post) {
    const { segmentSelected, groupSelected } = builderGroupData(action);
    const posts =
      state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
    const postIndex = postIndexByElementId(posts, likeActionElementId(action));
    if (postIndex >= 0) {
      reconcileReactToPost(
        state.getGroupPosts[groupSelected][segmentSelected].data.posts[
          postIndex
        ],
        action,
      );
    }
    const pg = state.getPost?.data?.post_group;
    if (pg && String(pg.id) === String(likeActionElementId(action))) {
      reconcileReactToPost(pg, action);
    }
    return;
  }

  for (const groupOption of [true, false]) {
    action.meta.arg.from_main_group = groupOption;
    const { segmentSelected, groupSelected } = builderGroupData(action);
    const posts =
      state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
    const postIndex = postIndexByElementId(posts, likeActionElementId(action));
    if (postIndex >= 0) {
      reconcileReactToPost(
        state.getGroupPosts[groupSelected][segmentSelected].data.posts[
          postIndex
        ],
        action,
      );
    }
  }
  reconcileReactToPost(state.getPost?.data?.post_group, action);
};

// Undo an optimistic reaction if the request fails — restores the pre-action
// like state the caller captured (prevLiked / prevReaction).
const revertReactToPost = (post, action) => {
  if (!post) return;
  const {
    delta = 0,
    prevLiked,
    prevReaction = null,
    willBeLiked,
    reaction_type,
  } = action.meta.arg;
  const failedReaction = willBeLiked ? reaction_type : null;
  post.likes_count = Math.max(0, (post.likes_count || 0) - delta);
  if (typeof prevLiked === "boolean") post.liked = prevLiked;
  post.my_reaction = prevReaction;

  if (failedReaction !== prevReaction) {
    post.reactions = transitionReactionCounts(
      post.reactions,
      failedReaction,
      prevReaction,
    );
  }
};

const revertReactPost = (state, action) => {
  if (!action.meta.arg.only_post) {
    const { segmentSelected, groupSelected } = builderGroupData(action);
    const posts =
      state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
    const postIndex = postIndexByElementId(posts, likeActionElementId(action));
    if (postIndex >= 0) {
      revertReactToPost(
        state.getGroupPosts[groupSelected][segmentSelected].data.posts[postIndex],
        action,
      );
    }
    const pg = state.getPost?.data?.post_group;
    if (pg && String(pg.id) === String(likeActionElementId(action))) {
      revertReactToPost(pg, action);
    }
  } else {
    for (const group_option of [true, false]) {
      action.meta.arg.from_main_group = group_option;
      const { segmentSelected, groupSelected } = builderGroupData(action);
      const posts =
        state.getGroupPosts[groupSelected][segmentSelected].data?.posts;
      const postIndex = postIndexByElementId(
        posts,
        likeActionElementId(action),
      );
      if (postIndex >= 0) {
        revertReactToPost(
          state.getGroupPosts[groupSelected][segmentSelected].data.posts[
            postIndex
          ],
          action,
        );
      }
    }
    revertReactToPost(state.getPost?.data?.post_group, action);
  }
};

const updateCommentCountPost = (state, action, value) => {
  const group_Array = [true, false];
  const segment_group_array = [true, false];

  for (const group_option of group_Array) {
    for (const segment_group_option of segment_group_array) {
      action.meta.arg.from_main_group = group_option;
      action.meta.arg.is_fixed = segment_group_option;
      updateCommentCountPostGroup(state, action, value);
    }
  }
  if (state.getPost?.data?.post_group) {
    state.getPost.data.post_group.comments_count += value;
  }
};

const increaseCommentCountPost = (state, action) =>
  updateCommentCountPost(state, action, 1);
const decreaseCommentCountPost = (state, action) =>
  updateCommentCountPost(state, action, -1);

const updateLikeComment = (state, action) => {
  const commentId = action.meta.arg.element_id;

  // Post detail comments (may not be loaded yet)
  const commentsArr = state.getPost?.data?.comments?.data;
  const commentIndex = Array.isArray(commentsArr)
    ? commentsArr.findIndex((comment) => comment.id === commentId)
    : -1;

  if (commentIndex >= 0) {
    const likedOldValue = commentsArr[commentIndex].liked;
    commentsArr[commentIndex].liked = !likedOldValue;
    commentsArr[commentIndex].likes_count += likedOldValue ? -1 : 1;
  }

  // Comment detail (replies screen)
  if (state.getCommentAndReplies?.data?.comment?.id === commentId) {
    const likedOldValue = state.getCommentAndReplies.data.comment.liked;
    state.getCommentAndReplies.data.comment.liked = !likedOldValue;
    state.getCommentAndReplies.data.comment.likes_count += likedOldValue
      ? -1
      : 1;
  }
};

const updateLikeReply = (state, action) => {
  const commentId = action.meta.arg.element_id;
  const repliesArr = state.getCommentAndReplies?.data?.replies?.data;
  if (!Array.isArray(repliesArr)) return;

  const replyIndex = repliesArr.findIndex(
    (comment) => comment.id === commentId,
  );
  if (replyIndex >= 0) {
    const likedOldValue = repliesArr[replyIndex].liked;
    repliesArr[replyIndex].liked = !likedOldValue;
    repliesArr[replyIndex].likes_count += likedOldValue ? -1 : 1;
  }
};

const initialState = {
  getGroupsList: {
    isLoading: true,
    error: null,
    data: null,
  },
  getGroupPosts: {
    main: {
      header: null,
      fixed: {
        isLoading: false,
        error: null,
        data: null,
      },
      general: {
        isLoading: false,
        error: null,
        data: null,
      },
    },
    not_main: {
      header: null,
      fixed: {
        isLoading: false,
        error: null,
        data: null,
      },
      general: {
        isLoading: false,
        error: null,
        data: null,
      },
    },
  },
  getPost: {
    isLoading: false,
    error: null,
    data: null,
  },
  addPost: {
    isLoading: false,
    error: null,
    data: null,
  },
  getCommentAndReplies: {
    isLoading: false,
    error: null,
    data: {
      comment: null,
      replies: {
        data: [],
        total: 0,
      },
    },
  },
  // Bumped on every `deletePost.fulfilled`. Local component state living in
  // `useFeedData` (paginated accumulator) and in `Segments` (newer-posts
  // buffer + focused-post buffer) subscribes to this and removes the
  // matching row, so the UI updates without waiting for a refresh.
  lastDeletedPostGroupId: null,
  // Bumped when a post's text is edited in place; useFeedData + Segments
  // mirror the new content/edited_at into their local accumulators.
  lastEditedPost: null,
  // Settings-only twin of `lastEditedPost`, for the comments/reactions toggles
  // in the post "..." menu. Deliberately carries NO content/edited_at: those
  // mirrors spread whatever they are given, and a settings toggle that shipped
  // `content: undefined` would blank the rendered post body.
  lastPostSettingsChange: null,
  // Optimistic moderation state, keyed by post-group id: { hidden_at,
  // visible_to_guest }.
  //
  // The feed does NOT render straight from the buckets above — Group/Segments
  // merges them with its own local accumulators, and a post served from that
  // buffer shadows the patched bucket copy. Comments/reactions get away with it
  // because Segments mirrors their signal into the buffer; Nascondi/Mostra
  // showed the moderator a card that hadn't changed until they refreshed, which
  // is precisely the confusion this feature exists to remove. Post reads these
  // overrides directly, so the card flips regardless of which source rendered
  // it. Cleared whenever fresh server data arrives.
  postModeration: {},
  // Latest `poll` block a vote returned, keyed by post-group id, as
  // { poll, at } (SABT-1989). Same reason as postModeration: the feed renders
  // from local accumulators a store patch never reaches, so a card can be
  // handed a poll from before the vote. usePostPoll/pickPoll compare the two
  // copies; an entry is NOT dropped when the feed refetches, because the
  // refetch may not reach the rendered list (it did, and the vote looked lost
  // after the app came back from standby). It is dropped when the session changes.
  postPolls: {},
};

const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    setPost: (state, action) => {
      state.getPost.data = action.payload;
    },
    setComment: (state, action) => {
      state.getCommentAndReplies.data.comment = action.payload;
    },
    // Update a post's text after an in-place edit (author / moderator).
    updatePostContent: (state, action) => {
      const {
        postGroupId,
        content,
        edited_at,
        comments_enabled,
        images,
        is_pinned,
        hidden_at,
        visible_to_guest,
      } = action.payload || {};
      const want = String(postGroupId);
      // Only overwrite the fields actually supplied — this action is reused to
      // patch just `images` (photo rotation), `is_pinned` (pin/unpin) or
      // `hidden_at` (Nascondi/Mostra) without wiping the content.
      const applyToPost = (post) => {
        if (!post) return;
        if (content !== undefined) post.content = content;
        if (edited_at !== undefined) post.edited_at = edited_at;
        if (comments_enabled !== undefined) post.comments_enabled = comments_enabled;
        if (images !== undefined) post.images = images;
        if (is_pinned !== undefined) post.is_pinned = is_pinned;
        if (hidden_at !== undefined) post.hidden_at = hidden_at;
        if (visible_to_guest !== undefined) post.visible_to_guest = visible_to_guest;
      };
      const applyToBucket = (bucket) => {
        const posts = bucket?.data?.posts;
        if (!Array.isArray(posts)) return;
        posts.forEach((p) => {
          if (String(p?.id) === want && p?.post) applyToPost(p.post);
        });
      };
      const root = state.getGroupPosts;
      applyToBucket(root?.main?.fixed);
      applyToBucket(root?.main?.general);
      applyToBucket(root?.not_main?.fixed);
      applyToBucket(root?.not_main?.general);
      const pg = state.getPost?.data?.post_group;
      if (pg && String(pg.id) === want) applyToPost(pg.post);
      // Only fire the content-mirror signal for ACTUAL text edits. Pin/unpin
      // and photo-rotation reuse this action with no `content`, and the local
      // feed accumulators (useFeedData / Segments) would otherwise mirror
      // `content: undefined` into the visible post and blank its body
      // ("LEGGI ANCORA"). The Redux buckets above are already patched in place.
      if (content !== undefined) {
        state.lastEditedPost = {
          postGroupId,
          content,
          edited_at,
          comments_enabled,
        };
      }
    },
    // Toggle comments/reactions on a post from the "..." menu. Separate from
    // `updatePostContent` on purpose: that action only signals the local feed
    // accumulators when `content` is present (blanking guard), so reusing it
    // here would patch Redux while the visible card kept the old state.
    updatePostSettings: (state, action) => {
      const {
        postGroupId,
        comments_enabled,
        reactions_enabled,
        hidden_at,
        visible_to_guest,
      } = action.payload || {};
      const want = String(postGroupId);
      const applyToPost = (post) => {
        if (!post) return;
        if (comments_enabled !== undefined)
          post.comments_enabled = comments_enabled;
        if (reactions_enabled !== undefined)
          post.reactions_enabled = reactions_enabled;
        if (hidden_at !== undefined) post.hidden_at = hidden_at;
        if (visible_to_guest !== undefined)
          post.visible_to_guest = visible_to_guest;
      };
      const applyToBucket = (bucket) => {
        const posts = bucket?.data?.posts;
        if (!Array.isArray(posts)) return;
        posts.forEach((p) => {
          if (String(p?.id) === want && p?.post) applyToPost(p.post);
        });
      };
      const root = state.getGroupPosts;
      applyToBucket(root?.main?.fixed);
      applyToBucket(root?.main?.general);
      applyToBucket(root?.not_main?.fixed);
      applyToBucket(root?.not_main?.general);
      const pg = state.getPost?.data?.post_group;
      if (pg && String(pg.id) === want) applyToPost(pg.post);
      // The rendered feed reads local accumulators, not these buckets — this
      // signal is what makes the visible card update without a refresh.
      state.lastPostSettingsChange = {
        postGroupId,
        comments_enabled,
        reactions_enabled,
        hidden_at,
        visible_to_guest,
      };
      if (hidden_at !== undefined || visible_to_guest !== undefined) {
        const current = state.postModeration[want] || {};
        state.postModeration[want] = {
          ...current,
          ...(hidden_at !== undefined ? { hidden_at } : {}),
          ...(visible_to_guest !== undefined ? { visible_to_guest } : {}),
        };
      }
    },
    // The poll block returned by a vote (or a "closed" discovery), so the card
    // shows the results without waiting for a refresh.
    setPostPoll: (state, action) => {
      const { postGroupId, poll, at } = action.payload;
      state.postPolls[String(postGroupId)] = { poll, at };
    },
    // Moderator "Nascondi" — drop the post from the current feed view (reuses
    // the delete-mirror signal); the backend keeps it visible to the author.
    hidePostFromFeed: (state, action) => {
      const postGroupId = action.payload;
      const want = String(postGroupId);
      const filterBucket = (bucket) => {
        const posts = bucket?.data?.posts;
        if (!Array.isArray(posts)) return;
        bucket.data.posts = posts.filter((p) => String(p?.id) !== want);
      };
      const root = state.getGroupPosts;
      filterBucket(root?.main?.fixed);
      filterBucket(root?.main?.general);
      filterBucket(root?.not_main?.fixed);
      filterBucket(root?.not_main?.general);
      state.lastDeletedPostGroupId = postGroupId;
    },
    // Immediately drop all posts authored by a just-blocked user (Guideline 1.2).
    // Durable filtering is enforced by the backend on the next feed fetch.
    hidePostsByUser: (state, action) => {
      const uid = String(action.payload);
      const authorOf = (p) =>
        String(
          p?.post?.created_by_user_id ??
            p?.post?.created_by?.id ??
            p?.created_by?.id ??
            p?.created_by_user_id ??
            "",
        );
      const filterBucket = (bucket) => {
        const posts = bucket?.data?.posts;
        if (!Array.isArray(posts)) return;
        bucket.data.posts = posts.filter((p) => authorOf(p) !== uid);
      };
      const root = state.getGroupPosts;
      filterBucket(root?.main?.fixed);
      filterBucket(root?.main?.general);
      filterBucket(root?.not_main?.fixed);
      filterBucket(root?.not_main?.general);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getGroupsList.pending, (state, action) => {
      state.getGroupsList.isLoading = true;
      state.getGroupsList.error = null;
    });
    builder.addCase(getGroupsList.fulfilled, (state, action) => {
      state.getGroupsList.isLoading = false;
      state.getGroupsList.data = action.payload;
    });
    builder.addCase(getGroupsList.rejected, (state, action) => {
      state.getGroupsList.error = action.error.message;
      state.getGroupsList.isLoading = false;
    });

    builder.addCase(getGroupPosts.pending, (state, action) => {
      const { segmentSelected, groupSelected } = builderGroupData(action);
      state.getGroupPosts[groupSelected][segmentSelected].isLoading = true;
      state.getGroupPosts[groupSelected][segmentSelected].error = null;
    });
    builder.addCase(getGroupPosts.fulfilled, (state, action) => {
      const { segmentSelected, groupSelected } = builderGroupData(action);

      state.getGroupPosts[groupSelected][segmentSelected].isLoading = false;
      // Fresh server data supersedes the optimistic moderation overrides —
      // otherwise a stale override would keep shadowing the real value.
      const page0 = action.meta.arg.page ?? 1;
      if (page0 === 1) state.postModeration = {};
      const incoming = action.payload;
      const page = action.meta.arg.page ?? 1;
      const prev = state.getGroupPosts[groupSelected][segmentSelected].data;
      const prevPosts = Array.isArray(prev?.posts) ? prev.posts : [];

      if (page === 1 || prevPosts.length === 0) {
        state.getGroupPosts[groupSelected][segmentSelected].data = incoming;
        return;
      }

      const incomingPosts = Array.isArray(incoming?.posts) ? incoming.posts : [];
      const prevIds = new Set(prevPosts.map((p) => String(p?.id)));
      const mergedPosts = [
        ...prevPosts,
        ...incomingPosts.filter((p) => p != null && !prevIds.has(String(p.id))),
      ];

      state.getGroupPosts[groupSelected][segmentSelected].data = {
        ...incoming,
        group: prev?.group ?? incoming?.group,
        posts: mergedPosts,
      };
    });
    builder.addCase(getGroupPosts.rejected, (state, action) => {
      const { segmentSelected, groupSelected } = builderGroupData(action);

      state.getGroupPosts[groupSelected][segmentSelected].error =
        action.error.message;
      state.getGroupPosts[groupSelected][segmentSelected].isLoading = false;
    });

    builder.addCase(likePost.pending, (state, action) => {
      updateLikePost(state, action);
    });
    builder.addCase(likePost.fulfilled, (state, action) => {});
    builder.addCase(likePost.rejected, (state, action) => {
      updateLikePost(state, action);
    });

    builder.addCase(reactToPost.pending, (state, action) => {
      updateReactPost(state, action);
    });
    builder.addCase(reactToPost.fulfilled, (state, action) => {
      reconcileReactPost(state, action);
    });
    builder.addCase(reactToPost.rejected, (state, action) => {
      revertReactPost(state, action);
    });

    builder.addCase(toggleFavoritePost.fulfilled, (state, action) => {
      updateCachedPostGroup(state, action.meta.arg.element_id, (post) => {
        post.favorited = !!action.payload?.favorited;
      });
    });

    builder.addCase(recordPostShare.fulfilled, (state, action) => {
      updateCachedPostGroup(state, action.meta.arg.element_id, (post) => {
        post.shares_count = Number(action.payload?.shares_count) || 0;
      });
    });

    builder.addCase(getPost.pending, (state, action) => {
      state.getPost.isLoading = true;
      state.getPost.error = null;
    });
    builder.addCase(getPost.fulfilled, (state, action) => {
      state.getPost.isLoading = false;
      state.getPost.data = action.payload;
    });
    builder.addCase(getPost.rejected, (state, action) => {
      state.getPost.isLoading = false;
      state.getPost.error = action.error.message;
      state.getPost.data = initialState.getPost.data;
    });

    builder.addCase(likeComment.pending, (state, action) => {
      const is_reply = action.meta.arg.is_reply;
      if (is_reply) {
        updateLikeReply(state, action);
      } else {
        updateLikeComment(state, action);
      }
    });
    builder.addCase(likeComment.fulfilled, (state, action) => {});
    builder.addCase(likeComment.rejected, (state, action) => {
      const is_reply = action.meta.arg.is_reply;
      if (is_reply) {
        updateLikeReply(state, action);
      } else {
        updateLikeComment(state, action);
      }
    });

    builder.addCase(commentPost.pending, (state, action) => {
      increaseCommentCountPost(state, action);
      createFakeComment(state, action);
    });
    builder.addCase(commentPost.fulfilled, (state, action) => {
      const commentsArr = state.getPost?.data?.comments?.data;
      if (!Array.isArray(commentsArr)) return;

      const newCommentIndex = commentsArr.findIndex(
        (comment) => comment.id === action.meta.arg.id,
      );
      if (newCommentIndex < 0) return;

      commentsArr[newCommentIndex] = {
        ...action.payload.comment,
        is_enabled: true,
        is_checked: false,
        likes_count: 0,
        reports_count: 0,
        replies_count: 0,
      };
    });
    builder.addCase(commentPost.rejected, (state, action) => {
      decreaseCommentCountPost(state, action);

      const commentsArr = state.getPost?.data?.comments?.data;
      if (!Array.isArray(commentsArr)) return;

      state.getPost.data.comments.data = commentsArr.filter(
        (comment) => comment.id !== action.meta.arg.id,
      );
    });

    builder.addCase(replyComment.pending, (state, action) => {
      increaseRepliesCountComment(state, action);
      createFakeReply(state, action);
    });
    builder.addCase(replyComment.fulfilled, (state, action) => {
      replaceFakeReplyforReal(state, action);
    });
    builder.addCase(replyComment.rejected, (state, action) => {
      decreaseRepliesCountComment(state, action);
      deleteFakeReply(state, action);
    });

    builder.addCase(getCommentAndReplies.pending, (state, action) => {
      state.getCommentAndReplies.isLoading = true;
      state.getCommentAndReplies.error = null;
    });
    builder.addCase(getCommentAndReplies.fulfilled, (state, action) => {
      state.getCommentAndReplies.isLoading = false;
      state.getCommentAndReplies.data = action.payload;
    });
    builder.addCase(getCommentAndReplies.rejected, (state, action) => {
      state.getCommentAndReplies.error = action.error.message;
      state.getCommentAndReplies.isLoading = false;
    });

    builder.addCase(addPost.pending, (state, action) => {
      state.addPost.isLoading = true;
      state.addPost.error = null;
    });
    builder.addCase(addPost.fulfilled, (state, action) => {
      state.addPost.isLoading = false;
      state.addPost.data = action.payload;
    });
    builder.addCase(addPost.rejected, (state, action) => {
      state.addPost.error = action.error.message;
      state.addPost.isLoading = false;
    });

    // When a user deletes their own post, the backend soft-deletes it
    // (sets is_enabled=false) so it's filtered out on the NEXT feed
    // fetch. Strip it from every group-feed bucket we currently cache
    // so it disappears from the UI immediately — and clear getPost.data
    // if the user is viewing the post detail page at the time.
    //
    // We also write the deleted id to `state.lastDeletedPostGroupId` so
    // the local-state buffers in `useFeedData` (paginated accumulator)
    // and `Segments` (newer-posts + focused-post) can react via
    // useEffect and trim themselves — without that, the Redux feed
    // gets filtered but the visible list (which renders from local
    // state) keeps the deleted post until the next refresh.
    builder.addCase(deletePost.fulfilled, (state, action) => {
      const postGroupId = action.payload?.postGroupId;
      if (!postGroupId) return;
      const want = String(postGroupId);

      const filterBucket = (bucket) => {
        const posts = bucket?.data?.posts;
        if (!Array.isArray(posts)) return;
        bucket.data.posts = posts.filter((p) => String(p?.id) !== want);
      };

      const root = state.getGroupPosts;
      filterBucket(root?.main?.fixed);
      filterBucket(root?.main?.general);
      filterBucket(root?.not_main?.fixed);
      filterBucket(root?.not_main?.general);

      if (
        state.getPost?.data?.post_group &&
        String(state.getPost.data.post_group.id) === want
      ) {
        state.getPost.data = null;
      }

      state.lastDeletedPostGroupId = postGroupId;
    });

    // A different member signing in must not inherit the previous one's votes.
    // Matched by type so this slice does not import the user slice (which
    // would be a circular dependency). Must stay after every addCase.
    builder.addMatcher(
      (action) => action.type === "user/setAdoc_t",
      (state) => {
        state.postPolls = {};
      },
    );
  },
});

export const {
  setPost,
  setComment,
  updatePostContent,
  updatePostSettings,
  setPostPoll,
  hidePostFromFeed,
  hidePostsByUser,
} =
  groupsSlice.actions;
export default groupsSlice.reducer;
